package com.writerslab

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * FAQ accordion at the foot of the main-page landing strip (see
 * LandingSection in Main.kt). Single-open — clicking a question collapses
 * whichever other one was expanded, so the block never becomes a wall of
 * text. Questions/answers come from LocalStrings.landingFaq; each answer
 * is markdown rendered through MarkdownBlock, so the shared `.wl-md` theme
 * handles paragraphs, bold and bullet lists for free.
 *
 * The chevron / clickable-header idiom mirrors AiInsightsSection so the
 * expand affordance reads the same across the app.
 */
@Composable
fun LandingFaq() {
    val s = LocalStrings.current
    val items = s.landingFaq
    if (items.isEmpty()) return

    // -1 = all collapsed; the FAQ starts closed so it sits quietly under
    // the demos rather than dominating the landing.
    var openIndex by remember { mutableStateOf(-1) }

    Div({ style { fillWidth(); flexCol(gapPx = 16) } }) {
        Span({
            style {
                typography(TextLevel.HeadlineSmall)
                fg(WLColors.OnSurface)
            }
        }) { Text(s.landingFaqTitle) }

        Div({ style { fillWidth(); flexCol(gapPx = 8) } }) {
            items.forEachIndexed { i, (question, answer) ->
                val open = openIndex == i
                Div({
                    style {
                        bg(WLColors.SurfaceContainerLow)
                        property("border-radius", "8px")
                        // A hairline that only lights up on the open card,
                        // so the expanded item stands out from its siblings.
                        property(
                            "border",
                            "1px solid ${if (open) WLColors.PrimaryContainer else "transparent"}",
                        )
                        fillWidth()
                        property("box-sizing", "border-box")
                    }
                }) {
                    // Clickable question row.
                    Div({
                        style {
                            flexRow(gapPx = 12)
                            fillWidth()
                            property("padding", "16px 18px")
                            property("box-sizing", "border-box")
                            clickableCursor()
                        }
                        onClick { openIndex = if (open) -1 else i }
                    }) {
                        Span({
                            style {
                                typography(TextLevel.TitleSmall)
                                fg(if (open) WLColors.Primary else WLColors.OnSurface)
                                property("flex", "1")
                                property("min-width", "0")
                            }
                        }) { Text(question) }
                        Span({
                            style {
                                typography(TextLevel.TitleMedium)
                                fg(WLColors.Primary)
                                property("flex-shrink", "0")
                                property("width", "16px")
                                property("text-align", "center")
                            }
                        }) { Text(if (open) "▾" else "▸") }
                    }

                    // Answer — markdown, only mounted while expanded.
                    if (open) {
                        MarkdownBlock(answer, attrs = {
                            classes("wl-fade-in", "wl-md")
                            style { property("padding", "0 18px 18px") }
                        })
                    }
                }
            }
        }
    }
}
